// composables/useConfirmModal.ts
import { ref } from 'vue';

interface ConfirmOptions {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  icon?: 'warning' | 'question' | 'info';
}

// Use a singleton pattern to ensure the same state is shared across components
const isVisible = ref(false);
const resolvePromise = ref<((value: boolean) => void) | null>(null);
const options = ref<ConfirmOptions>({
  title: 'Are you sure you want to proceed?',
  confirmText: 'Yes, confirm',
  cancelText: 'No, cancel',
  type: 'warning',
  icon: 'warning'
});

const DEFAULT_OPTIONS: ConfirmOptions = {
  title: 'Are you sure you want to proceed?',
  confirmText: 'Yes, confirm',
  cancelText: 'No, cancel',
  type: 'warning',
  icon: 'warning'
};

export function useConfirmModal() {
  const confirm = (opts: ConfirmOptions = {}): Promise<boolean> => {
    // Update options
    options.value = { ...DEFAULT_OPTIONS, ...opts };
    
    // Show the modal
    isVisible.value = true;
    
    // Return a promise that will be resolved when the user confirms or cancels
    return new Promise((resolve) => {
      resolvePromise.value = resolve;
    });
  };

  const handleConfirm = () => {
    isVisible.value = false;
    if (resolvePromise.value) {
      resolvePromise.value(true);
      resolvePromise.value = null;
    }
    options.value = { ...DEFAULT_OPTIONS };
  };

  const handleCancel = () => {
    isVisible.value = false;
    if (resolvePromise.value) {
      resolvePromise.value(false);
      resolvePromise.value = null;
    }
    options.value = { ...DEFAULT_OPTIONS }; 
  };

  return {
    isVisible,
    options,
    confirm,
    handleConfirm,
    handleCancel
  };
}